import { z } from 'zod'
import type { DefaultValues } from 'react-hook-form'
import type {
	BuildProfile,
	CreateBuildProfileBody,
	UpdateBuildProfileBody,
} from '@rentflowdev/api'

export const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/

const hexColorSchema = z
	.string()
	.regex(HEX_COLOR_REGEX, 'Цвет в формате #RRGGBB')

// branding.logo is either empty (default RentFlow logo) or the public https
// URL returned by POST /builder/upload-url — never a hand-typed path.
const logoUrlSchema = z
	.string()
	.url('Некорректный URL')
	.refine((value) => value.startsWith('https://'), 'Только https-ссылка')
	.or(z.literal(''))

/**
 * Strict subset of `CreateBuildProfileBody` (@rentflowdev/api): the form only
 * exposes consumer-facing fields. Technical identity (clientSlug,
 * applicationId, versions, backend config) is derived server-side and never
 * editable here.
 *
 * `features` renders only the production-ready toggles; the remaining flags
 * are always sent disabled (see `HIDDEN_FEATURES`).
 *
 * `rustoreKeyId` / `rustorePrivateKey` are write-only credentials: the backend
 * never returns them, so the form starts empty and empty values are omitted
 * from the request body (the backend keeps the stored pair on `undefined`).
 */
export const builderProfileSchema = z.object({
	appName: z
		.string()
		.trim()
		.min(2, 'Минимум 2 символа')
		.max(30, 'Максимум 30 символов'),
	branding: z.object({
		primaryColor:    hexColorSchema,
		accentColor:     hexColorSchema,
		backgroundColor: hexColorSchema,
		logo:            logoUrlSchema,
	}),
	features: z.object({
		payments:  z.boolean(),
		documents: z.boolean(),
		archive:   z.boolean(),
	}),
	storeMeta: z.object({
		shortDescription: z.string().trim().max(80, 'Максимум 80 символов'),
		fullDescription:  z.string().trim().max(4000, 'Максимум 4000 символов'),
		email:            z.string().trim().email('Некорректный email').or(z.literal('')),
	}),
	rustoreKeyId:      z.string().trim(),
	rustorePrivateKey: z.string().trim(),
}).superRefine((values, ctx) => {
	// The RuStore credentials only make sense as a pair.
	const hasKeyId      = values.rustoreKeyId !== ''
	const hasPrivateKey = values.rustorePrivateKey !== ''
	if (hasKeyId !== hasPrivateKey) {
		ctx.addIssue({
			code:    z.ZodIssueCode.custom,
			path:    [hasKeyId ? 'rustorePrivateKey' : 'rustoreKeyId'],
			message: 'Укажите оба поля ключа',
		})
	}
})

export type BuilderProfileFormValues = z.infer<typeof builderProfileSchema>

/** Feature flags that are not production-ready yet — hidden from the form and always sent disabled. */
const HIDDEN_FEATURES = {
	tickets:     false,
	chat:        false,
	negotiation: false,
	termination: false,
	kyc:         false,
	push:        false,
} as const

export const DEFAULT_PROFILE_VALUES: DefaultValues<BuilderProfileFormValues> = {
	appName: '',
	branding: {
		primaryColor:    '#2563EB',
		accentColor:     '#F59E0B',
		backgroundColor: '#FFFFFF',
		logo:            '',
	},
	features: {
		payments:  true,
		documents: true,
		archive:   true,
	},
	storeMeta: {
		shortDescription: '',
		fullDescription:  '',
		email:            '',
	},
	rustoreKeyId:      '',
	rustorePrivateKey: '',
}

/** Drops empty-string values so optional JSON fields stay clean on the wire. */
function stripEmpty<T extends Record<string, string>>(obj: T): Partial<T> {
	return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '')) as Partial<T>
}

/** Legacy profiles may store a repo path in `branding.logo` — the form only accepts https URLs. */
function toLogoUrl(logo: string | undefined): string {
	return logo?.startsWith('https://') ? logo : ''
}

export function profileToFormValues(profile: BuildProfile): BuilderProfileFormValues {
	return {
		appName: profile.appName,
		branding: {
			primaryColor:    profile.branding?.primaryColor ?? '',
			accentColor:     profile.branding?.accentColor ?? '',
			backgroundColor: profile.branding?.backgroundColor ?? '',
			logo:            toLogoUrl(profile.branding?.logo),
		},
		features: {
			payments:  profile.features?.payments ?? false,
			documents: profile.features?.documents ?? false,
			archive:   profile.features?.archive ?? false,
		},
		storeMeta: {
			shortDescription: profile.storeMeta?.shortDescription ?? '',
			fullDescription:  profile.storeMeta?.fullDescription ?? '',
			email:            profile.storeMeta?.email ?? '',
		},
		// Credentials are write-only and never returned — the fields stay empty.
		rustoreKeyId:      '',
		rustorePrivateKey: '',
	}
}

export function formValuesToCreateBody(values: BuilderProfileFormValues): CreateBuildProfileBody {
	return {
		appName:  values.appName,
		branding: values.branding,
		features: { ...values.features, ...HIDDEN_FEATURES },
		// Sent as a whole blob: emptied fields drop out so PATCH clears them.
		storeMeta: stripEmpty(values.storeMeta),
		// Empty credentials are omitted (`undefined` keeps the stored pair on
		// update); clearing is an explicit action that sends empty strings.
		...(values.rustoreKeyId !== '' && values.rustorePrivateKey !== ''
			? {
					rustoreKeyId:      values.rustoreKeyId,
					rustorePrivateKey: values.rustorePrivateKey,
				}
			: {}),
	}
}

export function formValuesToUpdateBody(values: BuilderProfileFormValues): UpdateBuildProfileBody {
	// The PATCH surface is a superset of create (all fields optional).
	return formValuesToCreateBody(values)
}
